import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";
import Modal from "../../../ui/Modal";
import stepsData from "./steps.json";
import KindsOfMomentStep, {
  validation as kindsOfMomentValidation,
} from "./KindsOfMoment";
import CategoryStep, { validation as categoryValidation } from "./Category";
import CountriesStep, { validation as countriesValidation } from "./Countries";
import SuccessStep from "./Success";
import Header from "./Header";
import { AlertModalContext } from "./context";
import Notification from "../../shared/Notification";
import LoginRequiredModal from "../../shared/LoginRequiredModal";
import { transformData, transformServiceTypes } from "./lib";
import { dataLayer } from "../../../../lib/analytics";

const stepComponents = {
  kinds_of_moment: KindsOfMomentStep,
  category: CategoryStep,
  countries: CountriesStep,
  success: SuccessStep,
};

const stepValidations = {
  kinds_of_moment: kindsOfMomentValidation,
  category: categoryValidation,
  countries: countriesValidation,
};

const INITIAL_STEP = "kinds_of_moment";
const EDIT_ALERT_STEP = "success";

const STEPS = stepsData.alert_modal;

const INITIAL_DATA = {
  kinds_of_moment: [],
  category: null,
  service_types: [],
  country_codes: [],
};

const AlertModal = ({
  isOpen,
  onClose,
  serviceTypes,
  jobAlert,
  setJobAlert,
  isArtistLoggedIn,
}) => {
  const initialStep = jobAlert ? EDIT_ALERT_STEP : INITIAL_STEP;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const initialData = jobAlert
    ? Object.keys(jobAlert).reduce((acc, key) => {
      if (key in INITIAL_DATA) {
        acc[key] = jobAlert[key];
      }
      return acc;
    }, {})
    : INITIAL_DATA;
  const [data, setData] = useState(initialData);
  const jobAlertId = jobAlert ? jobAlert.id : null;
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const previousStepName = useMemo(() => {
    return STEPS[currentStep] && STEPS[currentStep].previousStep;
  }, [currentStep]);
  const nextStepName = useMemo(() => {
    return STEPS[currentStep] && STEPS[currentStep].nextStep;
  }, [currentStep]);
  const progress = useMemo(() => {
    return STEPS[currentStep] && STEPS[currentStep].progress;
  }, [currentStep]);

  const eventAction = jobAlert ? "update" : "create";

  const transformedServiceTypes = useMemo(() => {
    return transformServiceTypes(serviceTypes);
  }, [serviceTypes]);

  const handleClose = (_e, resetToStep = initialStep) => {
    onClose();
    setTimeout(() => {
      // Just to stop a flicker of the first step when closing the modal.
      setCurrentStep(resetToStep);
    }, 100);
  };

  const isStepValid = () => {
    const validation = stepValidations[currentStep];
    if (!validation) {
      return true;
    }

    const newErrors = validation(data);
    if (Object.keys(newErrors).length === 0) {
      return true;
    }

    setErrors(newErrors);
    return false;
  };

  const sendForm = () => {
    if (isLoading) return;
    setIsLoading(true);
    const transformedData = transformData(data);
    $.ajax({
      type: jobAlertId ? "PATCH" : "POST",
      url: jobAlertId ? `/job-alerts/${jobAlertId}` : "/job-alerts",
      data: {
        job_alert: transformedData,
      },
      success: (response) => {
        setNotification({
          message: I18n.t("alert_modal.success.notification_message"),
          type: "success",
        });
        setTimeout(() => {
          setNotification({ message: "", type: "" });
        }, 4000);
        setJobAlert(response);
        const newData = Object.keys(response).reduce((acc, key) => {
          if (key in INITIAL_DATA) {
            acc[key] = response[key];
          }
          return acc;
        }, {});
        setData(newData);
        setCurrentStep(nextStepName);
        setIsLoading(false);
        const eventName = jobAlert ? "jobBoardAlertSettingsUpdated" : "jobBoardAlertSettingsCreated";
        dataLayer.push({
          event: eventName,
        });
      },
      error: () => {
        setNotification({
          message: I18n.t("alert_modal.success.error_message"),
          type: "error",
        });
        setTimeout(() => {
          setNotification({ message: "", type: "" });
        }, 5000);
        setIsLoading(false);
        const eventName = jobAlert ? "jobBoardAlertSettingsUpdateError" : "jobBoardAlertSettingsCreateError";
        dataLayer.push({
          event: eventName,
        });
      },
    });
  };

  const onNext = () => {
    if (isLoading) return;
    if (!isStepValid()) {
      return;
    }

    if (currentStep === EDIT_ALERT_STEP) {
      dataLayer.push({
        event: "jobBoardAlertSettingsEdit",
      });
      setCurrentStep(INITIAL_STEP);
      return;
    }

    if (nextStepName === EDIT_ALERT_STEP) {
      sendForm();
      return;
    }

    if (nextStepName) {
      dataLayer.push({
        event: "jobBoardAlertSettingsNext",
        step: currentStep,
        action: eventAction,
      });
      setCurrentStep(nextStepName);
    } else {
      handleClose();
    }
  };

  const onPrevious = () => {
    if (previousStepName) {
      setCurrentStep(previousStepName);
      dataLayer.push({
        event: "jobBoardAlertSettingsBack",
        step: currentStep,
        action: eventAction,
      });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    onNext();
  };

  const deleteAlert = () => {
    if (isLoading) return;
    if (window.confirm(I18n.t("alert_modal.success.delete_confirmation"))) {
      setIsLoading(true);
      $.ajax({
        type: "DELETE",
        url: `/job-alerts/${jobAlertId}`,
        success: () => {
          setNotification({
            message: I18n.t("alert_modal.success.deleted_notification_message"),
            type: "success",
          });
          setTimeout(() => {
            setNotification({ message: "", type: "" });
          }, 4000);
          setData(INITIAL_DATA);
          handleClose(null, INITIAL_STEP);
          setJobAlert(null);
          setIsLoading(false);
          dataLayer.push({
            event: "jobBoardAlertSettingsDeleted",
          });
        },
        error: () => {
          setNotification({
            message: I18n.t("alert_modal.success.error_message"),
            type: "error",
          });
          setTimeout(() => {
            setNotification({ message: "", type: "" });
          }, 4000);
          setIsLoading(false);
          dataLayer.push({
            event: "jobBoardAlertSettingsDeleteError",
          });
        },
      });
    }
  };

  const renderHeader = () => {
    if (currentStep === "success") {
      return null;
    }

    return (
      <Header
        title={I18n.t(`alert_modal.generic.${currentStep}`)}
        progress={progress}
      />
    );
  };

  const updateData = (newData) => {
    setData((prevData) => ({
      ...prevData,
      ...newData,
    }));
  };

  const clearError = (errorKey) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  const setError = (errorKey, errorMessage) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      newErrors[errorKey] = errorMessage;
      return newErrors;
    });
  };

  const contextValue = useMemo(() => {
    return {
      updateData,
      data,
      errors,
      clearError,
      setError,
      serviceTypes: transformedServiceTypes,
    };
  }, [data, errors, clearError, transformedServiceTypes]);

  if (!isArtistLoggedIn) {
    return (
      <LoginRequiredModal
        isOpen={isOpen}
        onClose={onClose}
        title={I18n.t("alert_modal.login_required.title")}
      />
    );
  }

  return (
    <AlertModalContext.Provider value={contextValue}>
      <Modal isOpen={isOpen} onClose={handleClose} extraClass="b-inquiry-modal">
        <form onSubmit={onSubmit} className="b-inquiry-modal__form">
          {renderHeader()}

          {Object.entries(stepComponents).map(([key, component]) => {
            if (key !== currentStep) return null;
            const StepComponent = component;

            if (!StepComponent || typeof StepComponent !== "function") {
              throw new Error(
                "The step component must export an object with `Component` and a `validation` properties",
              );
            }
            return <StepComponent />;
          })}
          {/* Footer */}
          <div className="b-react-modal__footer b-inquiry-modal__footer">
            {currentStep !== "success" && previousStepName && (
              <button
                className="b-button b-button--inverse b-button--transparent qa-smart-inquiry-back"
                type="button"
                disabled={isLoading}
                onClick={(e) => {
                  e.preventDefault();
                  onPrevious();
                }}
              >
                {I18n.t("smart_form.shared.back")}
              </button>
            )}
            {currentStep === "success" && (
              <>
                <button
                  className="b-button b-button--inverse b-button--transparent qa-smart-inquiry-back"
                  type="button"
                  disabled={isLoading}
                  onClick={() => {
                    deleteAlert();
                  }}
                >
                  {I18n.t("alert_modal.success.delete")}
                </button>
                <button
                  type="submit"
                  className="b-button qa-smart-inquiry-next"
                  disabled={isLoading}
                  style={{ marginLeft: "auto", order: "2" }}
                >
                  {I18n.t("alert_modal.success.edit_alert")}
                </button>
              </>
            )}
            {nextStepName && (
              <button
                type="submit"
                className="b-button qa-smart-inquiry-next"
                disabled={isLoading}
                style={{ marginLeft: "auto", order: "2" }}
              >
                {I18n.t("smart_form.shared.next")}
              </button>
            )}
          </div>
        </form>
      </Modal>
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
    </AlertModalContext.Provider>
  );
};

export default AlertModal;

AlertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serviceTypes: PropTypes.object.isRequired,
  jobAlert: PropTypes.object,
  setJobAlert: PropTypes.func.isRequired,
  isArtistLoggedIn: PropTypes.bool.isRequired,
};

AlertModal.defaultProps = {
  jobAlert: null,
};
